import React, { useState } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TurnstileBox, { turnstileSiteKey } from '../components/TurnstileBox';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SignupPage = () => {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: '',
    password: '',
    company_name: '',
    accept_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);
  const [robotToken, setRobotToken] = useState('');
  const [robotReset, setRobotReset] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.email || !form.password || !form.company_name) {
      setErr(t('signup.err_required'));
      return;
    }
    if (form.password.length < 8) {
      setErr(t('signup.err_password'));
      return;
    }
    if (!form.accept_terms) {
      setErr(t('signup.err_terms'));
      return;
    }
    if (turnstileSiteKey() && !robotToken) {
      setErr(t('signup.err_robot'));
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/auth/register`, {
        email: form.email,
        password: form.password,
        company_name: form.company_name,
        turnstile_token: robotToken || undefined,
        plan_payload: {
          plan: params.get('plan') || null,
          cycle: params.get('cycle') || 'monthly',
        },
      });
      setSent(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || t('signup.err_default'));
      setRobotReset((n) => n + 1); // the token was used up
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <div className="text-5xl mb-4">✉️</div>
          <h1 className="text-2xl font-semibold mb-2">{t('signup.sent_heading')}</h1>
          <p className="text-gray-600 mb-4">
            {t('signup.sent_body_prefix')}<b>{form.email}</b>{t('signup.sent_body_suffix')}
          </p>
          <p className="text-xs text-gray-500">{t('signup.sent_hint')}</p>
          <Link to="/login" className="text-sm text-blue-600 hover:underline mt-6 inline-block">
            {t('signup.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-semibold text-center mb-2">{t('signup.title')}</h1>
        <p className="text-sm text-gray-500 text-center mb-6">{t('signup.subtitle')}</p>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium">{t('signup.company_label')}</span>
            <input type="text" required className="input w-full mt-1"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder={t('signup.company_ph')} />
          </label>
          <label className="block">
            <span className="text-sm font-medium">{t('signup.email_label')}</span>
            <input type="email" required className="input w-full mt-1"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t('signup.email_ph')} />
          </label>
          <label className="block">
            <span className="text-sm font-medium">{t('signup.password_label')}</span>
            <input type="password" required minLength={8} className="input w-full mt-1"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.accept_terms}
              onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
              className="mt-1" />
            <span>
              {t('signup.terms_prefix')}
              <Link to="/terms" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{t('signup.terms_link')}</Link>
              {t('signup.terms_and')}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{t('signup.privacy_link')}</Link>
              {t('signup.terms_suffix')}
            </span>
          </label>
          <TurnstileBox onToken={setRobotToken} resetKey={robotReset} />
          {err && <div className="text-red-600 text-sm">{err}</div>}
          <button type="submit" disabled={submitting}
            className="btn-primary w-full py-2.5">
            {submitting ? t('signup.submitting') : t('signup.submit')}
          </button>
          <p className="text-center text-xs text-gray-500 pt-2">
            {t('signup.have_account')}{' '}
            <Link to="/login" className="text-blue-600 hover:underline">{t('signup.login')}</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;
